// clinicalExaminationDetails.tsx
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useGetPatientAssessmentDetailQuery } from "@/features/patients/infrastructure/api/patientsApi";
import type { CognitiveFunctionalDTO } from "@/features/patients/domain/dto/ClinicalExaminationDTO";
import { useParams } from "react-router-dom";
import { formatDateTimeShort } from "@/lib/dateUtils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import EyeIcon from "@/assets/visibility.svg?react";
import EarIcon from "@/assets/hearing.svg?react";
import LipsIcon from "@/assets/lips.svg?react";
import { Loader2 } from "lucide-react";
import { useDownloadAssessmentReports } from "@/features/patients/application/hooks/useDownloadPatientReport";
import ScreeningReportSection from "../../components/PatientReportSection";
import { textStyles } from "@/lib/textStyles";
import { AssessmentStatusPill } from "@/components/AssessmentStatusPill";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

// ============================================================================
// Enum label maps
// ============================================================================

const getSeverityLabels = (t: TFunction<"patients">): Record<string, string> => ({
  NORMAL: t("clinicalExaminationDetails.severityLabels.normal"),
  MILD: t("clinicalExaminationDetails.severityLabels.mild"),
  MODERATE: t("clinicalExaminationDetails.severityLabels.moderate"),
  SEVERE: t("clinicalExaminationDetails.severityLabels.severe"),
  PROFOUND: t("clinicalExaminationDetails.severityLabels.profound"),

  MILD_MODERATE: t("clinicalExaminationDetails.severityLabels.mildModerate"),
  MILD_DIFFICULTY: t("clinicalExaminationDetails.severityLabels.mildDifficulty"),

  MILD_LOSS: t("clinicalExaminationDetails.severityLabels.mildLoss"),
  MODERATE_LOSS: t("clinicalExaminationDetails.severityLabels.moderateLoss"),
  SEVERE_LOSS: t("clinicalExaminationDetails.severityLabels.severeLoss"),
  PROFOUND_LOSS: t("clinicalExaminationDetails.severityLabels.profoundLoss"),
  DEAF: t("clinicalExaminationDetails.severityLabels.deaf"),
});

const getVisionPartLabels = (t: TFunction<"patients">): Record<string, string> => ({
  WEARS_GLASSES: t("clinicalExaminationDetails.visionPartLabels.wearsGlasses"),
  USES_CONTACT_LENSES: t(
    "clinicalExaminationDetails.visionPartLabels.usesContactLenses",
  ),
  SENSITIVE_TO_LIGHT: t(
    "clinicalExaminationDetails.visionPartLabels.sensitiveToLight",
  ),
});

const getHearingPartLabels = (t: TFunction<"patients">): Record<string, string> => ({
  USES_HEARING_AID: t(
    "clinicalExaminationDetails.hearingPartLabels.usesHearingAid",
  ),
  HAS_COCHLEAR_IMPLANT: t(
    "clinicalExaminationDetails.hearingPartLabels.hasCochlearImplant",
  ),
  USES_SIGN_LANGUAGE_OR_WRITTEN: t(
    "clinicalExaminationDetails.hearingPartLabels.usesSignLanguageOrWritten",
  ),
});

const getSpeechPartLabels = (t: TFunction<"patients">): Record<string, string> => ({
  SPEAKS_INDEPENDENTLY: t(
    "clinicalExaminationDetails.speechPartLabels.speaksIndependently",
  ),
  USES_COMMUNICATION_BOARD_AAC: t(
    "clinicalExaminationDetails.speechPartLabels.usesCommunicationBoardAac",
  ),
  USES_GESTURES_CAREGIVER_ASSISTANCE: t(
    "clinicalExaminationDetails.speechPartLabels.usesGesturesCaregiverAssistance",
  ),
});

const getSleepLabels = (t: TFunction<"patients">): Record<string, string> => ({
  EXCELLENT: t("clinicalExaminationDetails.sleepLabels.excellent"),
  GOOD: t("clinicalExaminationDetails.sleepLabels.good"),
  POOR: t("clinicalExaminationDetails.sleepLabels.poor"),
});

const getMoodLabels = (t: TFunction<"patients">): Record<string, string> => ({
  NOT_AT_ALL_DEPRESSED: t(
    "clinicalExaminationDetails.moodLabels.notAtAllDepressed",
  ),
  SEVERAL_DAYS: t("clinicalExaminationDetails.moodLabels.severalDays"),
  NEARLY_EVERY_DAY: t("clinicalExaminationDetails.moodLabels.nearlyEveryDay"),
});

const getAnxietyLabels = (t: TFunction<"patients">): Record<string, string> => ({
  NOT_ANXIOUS: t("clinicalExaminationDetails.anxietyLabels.notAnxious"),
  MODERATELY_ANXIOUS: t(
    "clinicalExaminationDetails.anxietyLabels.moderatelyAnxious",
  ),
  VERY_ANXIOUS: t("clinicalExaminationDetails.anxietyLabels.veryAnxious"),
});

const getApathyLabels = (t: TFunction<"patients">): Record<string, string> => ({
  NO_APATHY: t("clinicalExaminationDetails.apathyLabels.noApathy"),
  SLIGHT_APATHY: t("clinicalExaminationDetails.apathyLabels.slightApathy"),
  MILD_APATHY: t("clinicalExaminationDetails.apathyLabels.mildApathy"),
  MODERATE_APATHY: t("clinicalExaminationDetails.apathyLabels.moderateApathy"),
  SEVERE_APATHY: t("clinicalExaminationDetails.apathyLabels.severeApathy"),
});

const formatEnum = (value?: string | null) => {
  if (!value) return "—";

  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const label = (map: Record<string, string>, key?: string | null) =>
  (key && map[key]) || formatEnum(key);

const mapParts = (map: Record<string, string>, parts?: string[]) =>
  parts?.map((p) => map[p] || p) ?? [];

const getCognitiveBooleanValues = (
  cognitive: CognitiveFunctionalDTO | null | undefined,
) => [
  cognitive?.memory?.forgets_recent_events,
  cognitive?.memory?.repeats_questions,
  cognitive?.executive_function?.difficulty_planning_tasks,
  cognitive?.executive_function?.difficulty_managing_finances,
  cognitive?.language?.difficulty_finding_words,
  cognitive?.language?.difficulty_understanding_speech,
  cognitive?.visuospatial?.gets_lost_in_familiar_places,
  cognitive?.visuospatial?.difficulty_recognizing_faces_or_objects,
  cognitive?.perception_sleep?.visual_hallucinations,
  cognitive?.perception_sleep?.acts_out_dreams,
  cognitive?.motor_symptoms?.tremors_or_stiffness,
  cognitive?.motor_symptoms?.slowed_movement_or_balance_issues,
];

const isCognitiveSectionEmpty = (
  cognitive: CognitiveFunctionalDTO | null | undefined,
): boolean => {
  if (!cognitive) return true;
  return getCognitiveBooleanValues(cognitive).every(
    (value) => value === null || value === undefined,
  );
};

// ============================================================================
// Helper Components
// ============================================================================

const MEDICAL_HISTORY_TITLE_COLOR = "#1B1D21";
const MEDICAL_HISTORY_VALUE_COLOR = "#5F646F";

const MEDICAL_HISTORY_KEY_STYLE = {
  ...textStyles.body,
  color: MEDICAL_HISTORY_TITLE_COLOR,
};

const MEDICAL_HISTORY_INFO_KEY_STYLE = {
  ...textStyles.body,
  color: MEDICAL_HISTORY_TITLE_COLOR,
  fontWeight: 700,
};

const MEDICAL_HISTORY_VALUE_STYLE = {
  ...textStyles.body,
  color: MEDICAL_HISTORY_VALUE_COLOR,
};

const MEDICAL_HISTORY_HEADER_STYLE = {
  ...textStyles.body,
  color: MEDICAL_HISTORY_TITLE_COLOR,
  fontWeight: 700,
};

interface Props {
  assessmentId: string;
  /** Show this assessment's own status pill (used for previous assessments) */
  showStatus?: boolean;
}

const InfoRow = ({
  label: lbl,
  value,
}: {
  label: string;
  value: string | React.ReactNode;
}) => (
  <div className="flex flex-col gap-1 py-0.5">
    <span style={MEDICAL_HISTORY_INFO_KEY_STYLE}>{lbl}</span>
    <span style={MEDICAL_HISTORY_VALUE_STYLE}>{value || "—"}</span>
  </div>
);

const BoolRow = ({
  label: lbl,
  value,
}: {
  label: string;
  value?: boolean | null;
}) => {
  const { t } = useTranslation("patients");
  const display =
    value === null || value === undefined
      ? "—"
      : value
        ? t("clinicalExaminationDetails.yes")
        : t("clinicalExaminationDetails.no");

  return (
    <div className="flex justify-between gap-4 py-0.5">
      <span style={MEDICAL_HISTORY_KEY_STYLE}>{lbl}</span>
      <span className="text-right" style={MEDICAL_HISTORY_VALUE_STYLE}>
        {display}
      </span>
    </div>
  );
};

const SensoryDetailRow = ({
  label: lbl,
  items,
}: {
  label: string;
  items: string[];
}) => (
  <div className="flex flex-col gap-1">
    <span style={{ ...textStyles.body, color: "#1B1D21", fontWeight: 700 }}>
      {lbl}
    </span>
    <ul className="list-inside list-disc space-y-0.5">
      {items.length > 0 ? (
        items.map((item, idx) => (
          <li key={idx} style={{ ...textStyles.body, color: "#5F646F" }}>
            {item}
          </li>
        ))
      ) : (
        <li style={{ ...textStyles.body, color: "#5F646F" }}>—</li>
      )}
    </ul>
  </div>
);

const CLINICAL_DETAIL_COLUMN_CARD = "m-px shadow-sm";

const ClinicalExaminationDetails: React.FC<Props> = ({
  assessmentId,
  showStatus = false,
}) => {
  const { t } = useTranslation("patients");
  const { patientId } = useParams<{ patientId: string }>();
  const {
    downloadClinicalExam,
    clinicalExamLoading,
  } = useDownloadAssessmentReports();

  const { data, isLoading } = useGetPatientAssessmentDetailQuery(
    { patientId: patientId as string, assessmentId },
    { skip: !patientId || !assessmentId, refetchOnMountOrArgChange: true },
  );

  const SEVERITY_LABELS = getSeverityLabels(t);
  const VISION_PART_LABELS = getVisionPartLabels(t);
  const HEARING_PART_LABELS = getHearingPartLabels(t);
  const SPEECH_PART_LABELS = getSpeechPartLabels(t);
  const SLEEP_LABELS = getSleepLabels(t);
  const MOOD_LABELS = getMoodLabels(t);
  const ANXIETY_LABELS = getAnxietyLabels(t);
  const APATHY_LABELS = getApathyLabels(t);

  if (isLoading) return <div className="p-6">{t("clinicalExaminationDetails.loading")}</div>;
  if (!data?.data) return <div className="p-6">{t("clinicalExaminationDetails.noDataFound")}</div>;

  const assessment = data.data.assessment;
  const sensory = data.data.accessibility_sensory;
  const caregiver = data.data.caregiver_questionnaire;

  const neuro = caregiver?.neurological_medical_history;
  const behavioral = caregiver?.behavioral_psychological_changes;
  const cognitive = caregiver?.cognitive_functional_indicators;
  const cognitiveIsEmpty = isCognitiveSectionEmpty(cognitive);
  const cognitiveValue = (value: boolean | null | undefined) =>
    cognitiveIsEmpty ? null : value;
  return (
    <>
    <Card className="px-5 mx-auto space-y-6 bg-background mb-10">
      {/* Header */}
      <div className="overflow-hidden">
        <div className="flex flex-col gap-2">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div style={textStyles.heading3}>
                {t("clinicalExaminationDetails.title")}
              </div>
              {showStatus && <AssessmentStatusPill status={assessment?.status} />}
            </div>
            <div className="ml-auto flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                className="border-primary text-primary bg-white hover:bg-white hover:text-primary cursor-pointer"
                disabled={clinicalExamLoading}
                onClick={() => {
                  if (!patientId) return;
                  void downloadClinicalExam(patientId, assessmentId);
                }}
              >
                {clinicalExamLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("clinicalExaminationDetails.downloading")}
                  </>
                ) : (
                  t("clinicalExaminationDetails.downloadPdf")
                )}
              </Button>
            </div>
          </div>

          <Separator />

          <div
            className="flex gap-2 items-center flex-wrap"
            style={{ ...textStyles.body, color: "#5F646F" }}
          >
            {assessment?.clinical_context_scheduled_start_at && (
              <span>
                {formatDateTimeShort(
                  assessment.clinical_context_scheduled_start_at,
                )}
              </span>
            )}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {(assessment as any)?.screening_required && (
              <div className="rounded-lg bg-success-light px-2 py-[5px] text-success">
                <span style={textStyles.body}>
                  {t("clinicalExaminationDetails.screeningRequired")}
                </span>
              </div>
            )}
          </div>

          {assessment?.screening_recommended_doctors &&
            assessment.screening_recommended_doctors.length > 0 && (
              <div className="flex flex-col gap-1">
                <h4
                  style={{ ...textStyles.body, color: "#1B1D21", fontWeight: 700 }}
                >
                  {assessment.screening_recommended_doctors.length === 1
                    ? t("clinicalExaminationDetails.doctorRecommendedSingular")
                    : t("clinicalExaminationDetails.doctorRecommendedPlural")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {assessment.screening_recommended_doctors.map((doctor) => (
                    <div key={doctor.id} className="group relative">
                      <div
                        className="cursor-help rounded-lg bg-[#E7EFFF] px-2 py-[5px] text-black"
                        style={textStyles.body}
                      >
                        {doctor.name}
                      </div>
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                        {doctor.designation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Sensory Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Vision */}
        <Card className={`${CLINICAL_DETAIL_COLUMN_CARD} gap-0`}>
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <EyeIcon className="h-5 w-5 text-blue-500" />
                <CardTitle style={{ ...textStyles.body, fontWeight: 700 }}>
                  {t("clinicalExaminationDetails.vision")}
                </CardTitle>
              </div>
              <p style={{ ...textStyles.body, color: "#5F646F" }}>
                {label(SEVERITY_LABELS, sensory?.vision?.severity)}
              </p>
            </div>
          </CardHeader>
          <CardContent className="mt-4">
            <SensoryDetailRow
              label={t("clinicalExaminationDetails.assistiveFunctional")}
              items={mapParts(
                VISION_PART_LABELS,
                sensory?.vision?.functional_parts,
              )}
            />
          </CardContent>
        </Card>

        {/* Hearing */}
        <Card className={`${CLINICAL_DETAIL_COLUMN_CARD} gap-0`}>
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <EarIcon className="h-5 w-5 text-green-500" />
                <CardTitle style={{ ...textStyles.body, fontWeight: 700 }}>
                  {t("clinicalExaminationDetails.hearing")}
                </CardTitle>
              </div>
              <p style={{ ...textStyles.body, color: "#5F646F" }}>
                {label(SEVERITY_LABELS, sensory?.hearing?.severity)}
              </p>
            </div>
          </CardHeader>
          <CardContent className="mt-4">
            <SensoryDetailRow
              label={t("clinicalExaminationDetails.assistiveFunctional")}
              items={mapParts(
                HEARING_PART_LABELS,
                sensory?.hearing?.functional_parts,
              )}
            />
          </CardContent>
        </Card>

        {/* Speech */}
        <Card className={`${CLINICAL_DETAIL_COLUMN_CARD} gap-0`}>
          <CardHeader className="pb-0">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <LipsIcon className="h-5 w-5 text-purple-500" />
                <CardTitle style={{ ...textStyles.body, fontWeight: 700 }}>
                  {t("clinicalExaminationDetails.speech")}
                </CardTitle>
              </div>
              <p style={{ ...textStyles.body, color: "#5F646F" }}>
                {label(SEVERITY_LABELS, sensory?.speech?.severity)}
              </p>
            </div>
          </CardHeader>
          <CardContent className="mt-4">
            <SensoryDetailRow
              label={t("clinicalExaminationDetails.communicationMethods")}
              items={mapParts(
                SPEECH_PART_LABELS,
                sensory?.speech?.functional_parts,
              )}
            />
          </CardContent>
        </Card>
      </div>

      {/* Medical History Accordion — collapsed by default */}
      <Accordion type="single" collapsible className="-mt-4">
        <AccordionItem value="medical-history" className="border-none">
          <AccordionTrigger
            className="w-full cursor-pointer justify-start gap-2 px-0 py-3 hover:no-underline [&_svg]:text-gray-700"
            style={MEDICAL_HISTORY_HEADER_STYLE}
          >
            {t("clinicalExaminationDetails.medicalHistory")}
          </AccordionTrigger>

          <AccordionContent className="pb-3">
            <div className="grid grid-cols-1 gap-4 px-px pb-px pt-1 md:grid-cols-3">
                  {/* Neurological */}
                  <Card className={CLINICAL_DETAIL_COLUMN_CARD}>
                    <CardHeader>
                      <CardTitle style={MEDICAL_HISTORY_HEADER_STYLE}>
                        {t("clinicalExaminationDetails.neurologicalMedicalHistory")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.priorDiagnosis")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.neurologicalDiagnosis")}
                          value={neuro?.prior_diagnosis?.has_diagnosis}
                        />
                        {neuro?.prior_diagnosis?.has_diagnosis && (
                          <>
                            <InfoRow
                              label={t("clinicalExaminationDetails.condition")}
                              value={neuro.prior_diagnosis.condition || "—"}
                            />
                            <InfoRow
                              label={t("clinicalExaminationDetails.year")}
                              value={
                                neuro.prior_diagnosis.year_of_diagnosis || "—"
                              }
                            />
                          </>
                        )}
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.strokeHistory")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.historyOfStroke")}
                          value={neuro?.stroke_history?.history_of_stroke}
                        />
                        {neuro?.stroke_history?.history_of_stroke && (
                          <InfoRow
                            label={t("clinicalExaminationDetails.years")}
                            value={neuro.stroke_history.stroke_years || "—"}
                          />
                        )}
                        <BoolRow
                          label={t("clinicalExaminationDetails.historyOfTia")}
                          value={neuro?.stroke_history?.history_of_tia}
                        />
                        {neuro?.stroke_history?.history_of_tia && (
                          <InfoRow
                            label={t("clinicalExaminationDetails.years")}
                            value={neuro.stroke_history.tia_years || "—"}
                          />
                        )}
                        <BoolRow
                          label={t("clinicalExaminationDetails.multipleStrokes")}
                          value={neuro?.stroke_history?.multiple_strokes}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.headInjury")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.significantHeadInjury")}
                          value={neuro?.head_injury?.significant_head_injury}
                        />
                        {neuro?.head_injury?.significant_head_injury && (
                          <>
                            <BoolRow
                              label={t("clinicalExaminationDetails.lossOfConsciousness")}
                              value={neuro.head_injury.loss_of_consciousness}
                            />
                            <InfoRow
                              label={t("clinicalExaminationDetails.year")}
                              value={neuro.head_injury.year || "—"}
                            />
                          </>
                        )}
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.cardiovascularRiskFactors")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.hypertension")}
                          value={
                            neuro?.cardiovascular_risk_factors?.hypertension
                          }
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.diabetes")}
                          value={neuro?.cardiovascular_risk_factors?.diabetes}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.highCholesterol")}
                          value={
                            neuro?.cardiovascular_risk_factors?.high_cholesterol
                          }
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.heartDisease")}
                          value={
                            neuro?.cardiovascular_risk_factors?.heart_disease
                          }
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.familyHistory")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.neurodegenerativeCondition")}
                          value={neuro?.family_history?.has_family_history}
                        />
                        {neuro?.family_history?.has_family_history && (
                          <InfoRow
                            label={t("clinicalExaminationDetails.relation")}
                            value={neuro.family_history.relation || "—"}
                          />
                        )}
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.onsetOfSymptoms")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.gradualProgression")}
                          value={neuro?.onset_of_symptoms?.gradual_progression}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.suddenOnset")}
                          value={neuro?.onset_of_symptoms?.sudden_onset}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.stepWiseDecline")}
                          value={neuro?.onset_of_symptoms?.step_wise_decline}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Behavioural */}
                  <Card className={CLINICAL_DETAIL_COLUMN_CARD}>
                    <CardHeader>
                      <CardTitle style={MEDICAL_HISTORY_HEADER_STYLE}>
                        {t("clinicalExaminationDetails.behaviouralPsychologicalChanges")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <InfoRow
                        label={t("clinicalExaminationDetails.sleepPast7Days")}
                        value={label(
                          SLEEP_LABELS,
                          behavioral?.sleep_past_7_days,
                        )}
                      />
                      <InfoRow
                        label={t("clinicalExaminationDetails.moodPast2Weeks")}
                        value={label(
                          MOOD_LABELS,
                          behavioral?.mood_past_2_weeks,
                        )}
                      />
                      <InfoRow
                        label={t("clinicalExaminationDetails.anxiety")}
                        value={label(
                          ANXIETY_LABELS,
                          behavioral?.anxiety_current,
                        )}
                      />
                      <InfoRow
                        label={t("clinicalExaminationDetails.interestInActivities")}
                        value={label(
                          APATHY_LABELS,
                          behavioral?.interest_in_activities_past_week,
                        )}
                      />
                    </CardContent>
                  </Card>

                  {/* Cognitive */}
                  <Card className={CLINICAL_DETAIL_COLUMN_CARD}>
                    <CardHeader>
                      <CardTitle style={MEDICAL_HISTORY_HEADER_STYLE}>
                        {t("clinicalExaminationDetails.cognitiveFunctionalIndicators")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.memory")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.forgetsRecentEvents")}
                          value={cognitiveValue(
                            cognitive?.memory?.forgets_recent_events,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.repeatsQuestions")}
                          value={cognitiveValue(
                            cognitive?.memory?.repeats_questions,
                          )}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.executiveFunction")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.difficultyPlanningTasks")}
                          value={cognitiveValue(
                            cognitive?.executive_function
                              ?.difficulty_planning_tasks,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.difficultyManagingFinances")}
                          value={cognitiveValue(
                            cognitive?.executive_function
                              ?.difficulty_managing_finances,
                          )}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.language")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.difficultyFindingWords")}
                          value={cognitiveValue(
                            cognitive?.language?.difficulty_finding_words,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.difficultyUnderstandingSpeech")}
                          value={cognitiveValue(
                            cognitive?.language?.difficulty_understanding_speech,
                          )}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.visuospatial")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.getsLostInFamiliarPlaces")}
                          value={cognitiveValue(
                            cognitive?.visuospatial
                              ?.gets_lost_in_familiar_places,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.difficultyRecognisingFacesObjects")}
                          value={cognitiveValue(
                            cognitive?.visuospatial
                              ?.difficulty_recognizing_faces_or_objects,
                          )}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.perceptionSleepBehaviour")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.visualHallucinations")}
                          value={cognitiveValue(
                            cognitive?.perception_sleep?.visual_hallucinations,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.actsOutDreams")}
                          value={cognitiveValue(
                            cognitive?.perception_sleep?.acts_out_dreams,
                          )}
                        />
                      </div>

                      <div>
                        <h4
                          className="mb-1"
                          style={MEDICAL_HISTORY_HEADER_STYLE}
                        >
                          {t("clinicalExaminationDetails.motorSymptoms")}
                        </h4>
                        <BoolRow
                          label={t("clinicalExaminationDetails.tremorsOrStiffness")}
                          value={cognitiveValue(
                            cognitive?.motor_symptoms?.tremors_or_stiffness,
                          )}
                        />
                        <BoolRow
                          label={t("clinicalExaminationDetails.slowedMovementBalanceIssues")}
                          value={cognitiveValue(
                            cognitive?.motor_symptoms
                              ?.slowed_movement_or_balance_issues,
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>

    <ScreeningReportSection
      patientId={patientId as string}
      assessmentId={assessmentId}
      status={assessment.status}
    />
    </>
  );
};

export default ClinicalExaminationDetails;
